import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { theme } from "../../theme";
import { clamp } from '@popmotion/popcorn';
import { transparentize } from 'polished';
import { textStyles } from "../Text";
import { isFunction } from "../../utils/is-function";
import { easeCurves, eases } from "../../motion/eases";
import { AnimatePresence, motion } from 'framer-motion';
import { usePrevious, useHasMounted } from "../../hooks";
export function CircularProgress(_ref) {
  var _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'brandPrimary' : _ref$color,
      valueProp = _ref.value,
      _ref$strokeWidth = _ref.strokeWidth,
      strokeWidth = _ref$strokeWidth === void 0 ? 2 : _ref$strokeWidth,
      _ref$background = _ref.background,
      background = _ref$background === void 0 ? true : _ref$background,
      _ref$track = _ref.track,
      track = _ref$track === void 0 ? true : _ref$track,
      _ref$size = _ref.size,
      sizeProp = _ref$size === void 0 ? 64 : _ref$size,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? true : _ref$label,
      other = _objectWithoutProperties(_ref, ["color", "value", "strokeWidth", "background", "track", "size", "label"]);

  var indeterminant = typeof valueProp === 'undefined';
  var size = sizeProp;
  var hasMounted = useHasMounted();
  var prevValueProp = usePrevious(valueProp);
  var value = indeterminant ? 0.25 : valueProp * 0.01;
  value = clamp(0.01, 1, value); // This ensures that the rounded corner
  // fully overlaps with its other end.

  if (value === 1) {
    value += 0.01;
  }

  var totalSize = size + strokeWidth;
  var r = size / 2;
  var c = Math.PI * size;
  var dashOffset = c * (1 - clamp(0, 1, value));
  var cx = totalSize / 2;
  var primaryColor = theme.colors.palette[color];

  var _useMemo = useMemo(function () {
    return {
      trackColor: transparentize(0.7, primaryColor),
      backgroundColor: transparentize(0.9, primaryColor)
    };
  }, [primaryColor]),
      trackColor = _useMemo.trackColor,
      backgroundColor = _useMemo.backgroundColor;

  var accessibilityProps = !indeterminant && {
    'aria-valuenow': Math.round(valueProp),
    'aria-valuemin': 0,
    'aria-valuemax': 100
  };
  return /*#__PURE__*/React.createElement(RelativeContainer, {
    style: {
      height: "".concat(size, "px"),
      width: "".concat(size, "px")
    }
  }, /*#__PURE__*/React.createElement(Container, _extends({
    role: "progressbar"
  }, accessibilityProps, {
    strokeLinecap: "round",
    width: size,
    height: size,
    $indeterminate: indeterminant,
    viewBox: "0 0 ".concat(totalSize, " ").concat(totalSize)
  }, other), /*#__PURE__*/React.createElement(Circle, {
    cx: cx,
    cy: cx,
    r: r - strokeWidth / 2,
    fill: background ? backgroundColor : 'transparent'
  }), track && /*#__PURE__*/React.createElement(Circle, {
    cx: cx,
    cy: cx,
    r: r,
    fill: "none",
    stroke: trackColor,
    strokeWidth: strokeWidth
  }), /*#__PURE__*/React.createElement(Circle, {
    cx: cx,
    cy: cx,
    r: r,
    fill: "none",
    stroke: primaryColor,
    strokeDasharray: c,
    strokeDashoffset: dashOffset,
    strokeWidth: strokeWidth,
    style: {
      // disable animations when our value gets reset
      transition: valueProp < prevValueProp ? 'none' : undefined
    }
  })), /*#__PURE__*/React.createElement(AnimatePresence, null, label && valueProp > 0 && /*#__PURE__*/React.createElement(ProgressLabel, {
    initial: hasMounted ? {
      opacity: 0
    } : false,
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.3,
      ease: eases.primary
    },
    "aria-hidden": true,
    $isCustomLabel: isFunction(label),
    $size: size
  }, isFunction(label) ? label(valueProp) : /*#__PURE__*/React.createElement(React.Fragment, null, valueProp.toFixed(), /*#__PURE__*/React.createElement(Percentage, null, "%")))));
}
var Percentage = styled.span.withConfig({
  displayName: "CircularProgress__Percentage",
  componentId: "vapor__ayph3d-0"
})(["padding-left:1px;font-size:8px;color:", ";"], theme.colors.palette.whiteA70);
var ProgressLabel = styled(motion.div).withConfig({
  displayName: "CircularProgress__ProgressLabel",
  componentId: "vapor__ayph3d-1"
})(["", ";color:", ";pointer-events:none;user-select:none;position:absolute;top:50%;left:50%;display:flex;align-items:flex-start;font-variant-numeric:tabular-nums;transform:translateY(-50%) translateX(", ");", ""], textStyles.primary, theme.colors.palette.whiteA90, function (p) {
  return p.$isCustomLabel ? "-50%" : "calc(-50% + 2px)";
}, function (p) {
  return p.$size < 24 && "font-size: 10px;";
});
var RelativeContainer = styled.div.withConfig({
  displayName: "CircularProgress__RelativeContainer",
  componentId: "vapor__ayph3d-2"
})(["position:relative;"]);
var spin = keyframes(["from{transform:rotate(0deg);}to{transform:rotate(360deg);}"]);
var Container = styled.svg.withConfig({
  displayName: "CircularProgress__Container",
  componentId: "vapor__ayph3d-3"
})(["transform:rotate(-90deg);animation:", ";"], function (p) {
  return p.$indeterminate && css(["", " 0.7s linear infinite"], spin);
});
var Circle = styled.circle.withConfig({
  displayName: "CircularProgress__Circle",
  componentId: "vapor__ayph3d-4"
})(["will-change:stroke-dashoffset;transition:stroke-dashoffset 0.35s linear,stroke 0.3s ", ",fill 0.3s ", ";"], easeCurves.primary, easeCurves.primary);